<template>
  <PartnersLayout>
    <template #header>
      <PartnersHeader
        title="점프 효과 통계"
        subtitle="점프 노출 및 전환 데이터"
      />
    </template>

    <div class="px-4 lg:px-6 pb-6">
      <div class="max-w-6xl mx-auto">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- 왼쪽: 점프 목록 & 바로가기 -->
          <div class="lg:col-span-1 space-y-4">
            <!-- 점프 선택 -->
            <div class="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div class="p-4 border-b border-slate-100">
              <h3 class="font-bold text-slate-800 flex items-center gap-2">
                <svg class="w-5 h-5 text-violet-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                </svg>
                점프 목록
              </h3>
            </div>

            <div v-if="loading" class="p-4 text-center">
              <div class="w-8 h-8 border-4 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto"></div>
            </div>

            <div v-else-if="myJumps.length === 0" class="p-6 text-center">
              <p class="text-sm text-slate-500">점프 내역이 없습니다</p>
            </div>

            <div v-else class="max-h-80 overflow-y-auto">
              <button
                v-for="jump in myJumps"
                :key="jump.id"
                @click="selectJump(jump.id)"
                :class="[
                  'w-full p-4 text-left border-b border-slate-100 last:border-0 transition',
                  selectedJumpId === jump.id ? 'bg-violet-50' : 'hover:bg-slate-50'
                ]"
              >
                <div class="flex items-center justify-between">
                  <div>
                    <p :class="[
                      'font-medium text-sm',
                      selectedJumpId === jump.id ? 'text-violet-700' : 'text-slate-700'
                    ]">
                      {{ jump.propertyName }}
                    </p>
                    <p class="text-xs text-slate-400 mt-0.5">
                      {{ formatDateShort(jump.startDate) }} ~ {{ formatDateShort(jump.endDate) }}
                    </p>
                  </div>
                  <span :class="getStatusBadgeClass(getJumpStatus(jump))">
                    {{ getStatusLabel(getJumpStatus(jump)) }}
                  </span>
                </div>
              </button>
            </div>
          </div>

            <!-- 바로가기 -->
            <div class="bg-white border border-slate-200 rounded-2xl p-4">
              <p class="text-[12px] font-bold text-slate-500 mb-3">바로가기</p>
            <div class="space-y-2">
              <router-link
                to="/partners/jump/purchase"
                class="flex items-center justify-between p-3 bg-violet-50 rounded-xl hover:bg-violet-100 transition"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                    </svg>
                  </div>
                  <span class="text-[13px] font-medium text-violet-700">점프 신청하기</span>
                </div>
                <svg class="w-4 h-4 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </router-link>
              <router-link
                to="/partners/jump/my"
                class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  </div>
                  <span class="text-[13px] font-medium text-slate-700">내 점프 관리</span>
                </div>
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </router-link>
              <router-link
                to="/partners/leads/banner"
                class="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition"
              >
                <div class="flex items-center gap-3">
                  <div class="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <svg class="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                    </svg>
                  </div>
                  <span class="text-[13px] font-medium text-slate-700">광고 유입 콜</span>
                </div>
                <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                </svg>
              </router-link>
              </div>
            </div>
          </div>

          <!-- 오른쪽: 통계 상세 -->
          <div class="lg:col-span-2">
            <!-- 점프 미선택 -->
            <div v-if="!selectedJump" class="bg-white border border-slate-200 rounded-2xl p-12 text-center">
            <div class="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
              </svg>
            </div>
            <h3 class="text-lg font-semibold text-slate-700 mb-2">점프를 선택하세요</h3>
            <p class="text-slate-500">왼쪽 목록에서 점프를 선택하면 통계를 확인할 수 있습니다</p>
          </div>

            <!-- 통계 상세 -->
            <div v-else class="space-y-4">
              <!-- 점프 정보 카드 -->
              <div class="bg-white border border-slate-200 rounded-2xl p-5">
              <div class="flex items-start justify-between mb-4">
                <div>
                  <h3 class="text-lg font-bold text-slate-800">{{ selectedJump.propertyName }}</h3>
                  <p class="text-sm text-slate-500 mt-1">
                    {{ formatDate(selectedJump.startDate) }} ~ {{ formatDate(selectedJump.endDate) }}
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <span class="px-3 py-1 text-sm font-medium bg-green-100 text-green-700 rounded-full">무료</span>
                  <span :class="getStatusBadgeClass(getJumpStatus(selectedJump))">
                    {{ getStatusLabel(getJumpStatus(selectedJump)) }}
                  </span>
                </div>
              </div>
              <div>
                <span class="px-3 py-1 text-sm bg-violet-50 text-violet-600 rounded-full">
                  {{ selectedJump.timeSlot?.label || `${selectedJump.timeSlot?.start} ~ ${selectedJump.timeSlot?.end}` }}
                </span>
              </div>
            </div>

              <!-- 통계 카드 3개 -->
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <!-- 노출 수 -->
                <div class="bg-white border border-slate-200 rounded-2xl p-5">
                <div class="flex items-center gap-3 mb-3">
                  <div class="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                    <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                    </svg>
                  </div>
                  <span class="text-sm text-slate-500">노출 수</span>
                </div>
                <p class="text-3xl font-bold text-slate-800">{{ stats.impressions.toLocaleString() }}</p>
                <p v-if="stats.prevImpressions > 0" class="text-sm mt-2" :class="getChangeClass(stats.impressions, stats.prevImpressions)">
                  {{ getChangeText(stats.impressions, stats.prevImpressions) }} (이전 대비)
                </p>
              </div>

                <!-- 상담 신청 수 -->
                <div class="bg-white border border-slate-200 rounded-2xl p-5">
                <div class="flex items-center gap-3 mb-3">
                  <div class="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                    <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                    </svg>
                  </div>
                  <span class="text-sm text-slate-500">상담 신청</span>
                </div>
                <p class="text-3xl font-bold text-slate-800">{{ stats.consultationRequests }}</p>
                <p v-if="stats.prevRequests > 0" class="text-sm mt-2" :class="getChangeClass(stats.consultationRequests, stats.prevRequests)">
                  {{ getChangeText(stats.consultationRequests, stats.prevRequests) }} (이전 대비)
                </p>
              </div>

                <!-- 전환율 -->
                <div class="bg-white border border-slate-200 rounded-2xl p-5">
                <div class="flex items-center gap-3 mb-3">
                  <div class="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <svg class="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                    </svg>
                  </div>
                  <span class="text-sm text-slate-500">전환율</span>
                </div>
                <p class="text-3xl font-bold text-slate-800">{{ stats.conversionRate.toFixed(1) }}%</p>
                <p class="text-sm text-slate-400 mt-2">노출 대비 신청 비율</p>
              </div>
            </div>

              <!-- 효과 분석 -->
              <div class="bg-violet-50 border border-violet-200 rounded-2xl p-5">
              <h4 class="font-semibold text-violet-800 mb-3 flex items-center gap-2">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                </svg>
                점프 효과 분석
              </h4>
              <ul class="text-sm text-violet-700 space-y-2">
                <li v-if="stats.impressions > 0 && stats.prevImpressions > 0 && stats.impressions > stats.prevImpressions" class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>노출이 {{ Math.round((stats.impressions / stats.prevImpressions - 1) * 100) }}% 증가했습니다!</span>
                </li>
                <li v-if="stats.consultationRequests > 0 && stats.prevRequests >= 0 && stats.consultationRequests > stats.prevRequests" class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span>상담 신청이 {{ stats.consultationRequests - stats.prevRequests }}건 더 발생했습니다!</span>
                </li>
                <li v-if="stats.conversionRate > 0" class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                  </svg>
                  <span>전환율 {{ stats.conversionRate.toFixed(1) }}%는 {{ stats.conversionRate > 3 ? '평균보다 높은' : '보통' }} 수준입니다.</span>
                </li>
                <li v-if="stats.impressions === 0" class="flex items-start gap-2">
                  <svg class="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                  </svg>
                  <span>아직 노출 데이터가 수집되지 않았습니다. 점프가 시작되면 데이터가 쌓입니다.</span>
                </li>
              </ul>
            </div>

              <!-- 안내 문구 -->
              <div class="bg-slate-50 border border-slate-200 rounded-2xl p-4">
                <p class="text-xs text-slate-500 text-center">
                  통계는 실시간으로 업데이트되며, 점프 시간대에만 노출 수가 집계됩니다.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PartnersLayout>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import PartnersLayout from '@/components/partners/common/PartnersLayout.vue'
import PartnersHeader from '@/components/partners/common/PartnersHeader.vue'
import { useJump } from '@/composables/partners/useJump'
import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase/firebase'

export default {
  name: 'JumpStatsView',
  components: {
    PartnersLayout,
    PartnersHeader
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { myJumps, loading, loadMyJumps, getJumpStatus } = useJump()

    const selectedJumpId = computed(() => route.query.id)
    const selectedJump = ref(null)
    const stats = ref({
      impressions: 0,
      consultationRequests: 0,
      conversionRate: 0,
      prevImpressions: 0,
      prevRequests: 0
    })

    const selectJump = (jumpId) => {
      router.push({ path: '/partners/jump/stats', query: { id: jumpId } })
    }

    const getStatusLabel = (status) => {
      const labels = {
        active: '활성',
        expired: '종료',
        cancelled: '취소됨'
      }
      return labels[status] || status
    }

    const getStatusBadgeClass = (status) => {
      const classes = {
        active: 'px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full',
        expired: 'px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-full',
        cancelled: 'px-2 py-0.5 text-xs font-medium bg-red-100 text-red-600 rounded-full'
      }
      return classes[status] || classes.active
    }

    const getChangeClass = (current, prev) => {
      if (current > prev) return 'text-green-600'
      if (current < prev) return 'text-red-600'
      return 'text-slate-500'
    }

    const getChangeText = (current, prev) => {
      if (prev === 0) return '+' + current
      const change = Math.round((current / prev - 1) * 100)
      return change >= 0 ? `+${change}%` : `${change}%`
    }

    const formatDateShort = (dateStr) => {
      if (!dateStr) return ''
      const [, m, d] = dateStr.split('-')
      return `${parseInt(m)}/${parseInt(d)}`
    }

    const formatDate = (dateStr) => {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const loadJumpData = async () => {
      if (!selectedJumpId.value) {
        selectedJump.value = null
        stats.value = {
          impressions: 0,
          consultationRequests: 0,
          conversionRate: 0,
          prevImpressions: 0,
          prevRequests: 0
        }
        return
      }

      try {
        // 점프 정보 로드
        const jumpDoc = await getDoc(doc(db, 'jumpSlots', selectedJumpId.value))
        if (jumpDoc.exists()) {
          selectedJump.value = { id: jumpDoc.id, ...jumpDoc.data() }
        }

        // 통계 로드
        const statsDoc = await getDoc(doc(db, 'jumpStats', selectedJumpId.value))
        if (statsDoc.exists()) {
          stats.value = statsDoc.data()
        } else {
          // 통계 없으면 기본값
          stats.value = {
            impressions: 0,
            consultationRequests: 0,
            conversionRate: 0,
            prevImpressions: 0,
            prevRequests: 0
          }
        }
      } catch (err) {
        console.error('데이터 로드 실패:', err)
      }
    }

    watch(selectedJumpId, () => {
      loadJumpData()
    })

    onMounted(async () => {
      await loadMyJumps()
      await loadJumpData()
    })

    return {
      myJumps,
      loading,
      selectedJumpId,
      selectedJump,
      stats,
      selectJump,
      getJumpStatus,
      getStatusLabel,
      getStatusBadgeClass,
      getChangeClass,
      getChangeText,
      formatDateShort,
      formatDate
    }
  }
}
</script>
